import { Fragment, useEffect, useState, type FC, type ReactNode } from 'react';
import { useAtom } from 'jotai';
import {
  DataTable,
  type DataTableRenderProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableExpandedRow,
  TableExpandHeader,
  TableExpandRow,
  InlineLoading,
  InlineNotification,
  Tag,
} from '@carbon/react';
import moment from 'moment';
import { formatNumber } from 'utils/number';
import { formatMutationResource } from 'utils/misc';
import type { PreDrillAgentRun } from 'types/api-responses';
import { preDrillAgentOutputAtom } from './atoms';
import { pctScore, formatSrpWellName, actorDisplayName } from './utils';
import styles from './agent-output-table.module.scss';

interface AgentOutputTableProps {
  uwi14: string;
}

const HEADERS = [
  { key: 'sequence', header: 'Sequence' },
  { key: 'dataCategory', header: 'Data Category' },
  { key: 'action', header: 'Action' },
  { key: 'summary', header: 'Summary' },
  { key: 'user', header: 'User' },
  { key: 'date', header: 'Date' },
];

interface DisplayRow {
  id: string;
  sequence: number;
  dataCategory: string;
  action: string;
  summary: string;
  user: string;
  date: string;
}

// Human labels for the agent's snake_case enum fields (fall back to title-case).
const DATA_CATEGORY_LABELS: Record<string, string> = {
  target_zone_suggestion: 'Target Zone Match',
};
const humanize = (value: string): string =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Per-run derivations (shared by row cells + expanded panels).
const inputStr = (
  inputs: Record<string, unknown>,
  key: string
): string | null => {
  const value = inputs[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
};
const inputNum = (
  inputs: Record<string, unknown>,
  key: string
): number | null => {
  const value = inputs[key];
  return typeof value === 'number' ? value : null;
};
const ftOrDash = (n: number | null): string => {
  return n === null ? '—' : `${formatNumber(n, 0, 0)} ft`;
};

// Depth interval label (e.g. "9,400–9,750 ft TVD"), or null when unresolved.
const depthLabel = (depth: {
  topTvdFt: number | null;
  bottomTvdFt: number | null;
}): string | null => {
  const { topTvdFt: top, bottomTvdFt: bottom } = depth;
  if (top !== null && bottom !== null) {
    return `${formatNumber(top, 0, 0)}–${formatNumber(bottom, 0, 0)} ft TVD`;
  }
  if (top !== null) return `${formatNumber(top, 0, 0)} ft TVD`;
  if (bottom !== null) return `${formatNumber(bottom, 0, 0)} ft TVD`;
  return null;
};

const runSummary = (run: PreDrillAgentRun): string => {
  const top = run.output.topThree[0];
  if (!top) return '—';
  const range = depthLabel(top.depthRange);
  return `Identified ${top.geologicIntervalName}${
    range ? ` · ${range}` : ''
  } as primary target`;
};

const userLabel = (run: PreDrillAgentRun): string => {
  const actor = actorDisplayName(run.actorType, run.actorName);
  return run.output.autoConfirmed ? `Auto-confirmed by ${actor}` : actor;
};

const formatRunDate = (iso: string): string => {
  const m = moment(iso);
  return m.isValid() ? m.format('M/D/YYYY, h:mm:ss A') : iso;
};

// Inputs panel: permit + operator context the agent ran against (run's
// `inputs` snapshot). Empty for auto-provision seed runs.
const InputsPanel: FC<{ run: PreDrillAgentRun }> = ({ run }) => {
  const inputs = run.inputs as Record<string, unknown>;
  const fields: Array<{ label: string; value: string }> = [
    {
      label: 'Permit',
      value:
        formatSrpWellName(
          inputStr(inputs, 'wellName'),
          inputStr(inputs, 'wellNum')
        ) ?? '—',
    },
    { label: 'UWI14', value: inputStr(inputs, 'uwi14') ?? run.uwi14 },
    { label: 'Operator', value: inputStr(inputs, 'currentOperator') ?? '—' },
    {
      label: 'Permitted MD',
      value: ftOrDash(inputNum(inputs, 'permittedDepthFt')),
    },
    {
      label: 'Permitted TVD',
      value: ftOrDash(inputNum(inputs, 'permittedDepthTvdFt')),
    },
    {
      label: 'Hole Direction',
      value: inputStr(inputs, 'permitHoleDirection') ?? '—',
    },
    {
      label: 'Target Formation (permit)',
      value: inputStr(inputs, 'permitTargetFormation') ?? '—',
    },
    {
      label: 'Structure Grid Collection',
      value:
        inputStr(inputs, 'structureGridCollectionName') ??
        (inputNum(inputs, 'structureGridCollectionId') !== null
          ? `#${inputNum(inputs, 'structureGridCollectionId')}`
          : '—'),
    },
  ];

  return (
    <section className={styles.panel}>
      <h4 className={styles.panelHeading}>Inputs</h4>
      {Object.keys(inputs).length === 0 ? (
        <p className={styles.muted}>No inputs recorded for this run.</p>
      ) : (
        <>
          <p className={styles.panelDesc}>
            Permit &amp; operator context handed to the agent at run time.
          </p>
          <dl className={styles.fields}>
            {fields.map((field) => (
              <div key={field.label} className={styles.field}>
                <dt>{field.label}</dt>
                <dd>{field.value}</dd>
              </div>
            ))}
          </dl>
        </>
      )}
    </section>
  );
};

// Reasoning panel: the agent's narrative + the key evidence bullets.
const ReasoningPanel: FC<{ run: PreDrillAgentRun }> = ({ run }) => {
  const { narrativeSummary, keyEvidence } = run.reasoning;
  if (!narrativeSummary && keyEvidence.length === 0) {
    return (
      <section className={styles.panel}>
        <h4 className={styles.panelHeading}>Reasoning</h4>
        <p className={styles.muted}>No reasoning recorded for this run.</p>
      </section>
    );
  }
  return (
    <section className={styles.panel}>
      <h4 className={styles.panelHeading}>Reasoning</h4>
      {narrativeSummary && (
        <p className={styles.panelText}>{narrativeSummary}</p>
      )}
      {keyEvidence.length > 0 && (
        <ul className={styles.evidence}>
          {keyEvidence.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

// Output panel: the ranked candidate zones the run surfaced (rank 1 = primary).
const OutputPanel: FC<{ run: PreDrillAgentRun }> = ({ run }) => {
  const picks = run.output.topThree;
  return (
    <section className={styles.panel}>
      <h4 className={styles.panelHeading}>Output</h4>
      {picks.length === 0 ? (
        <p className={styles.muted}>No candidate zones surfaced.</p>
      ) : (
        <>
          <p className={styles.panelDesc}>
            {picks.length} ranked candidate{picks.length === 1 ? '' : 's'}{' '}
            surfaced to Match Review.
          </p>
          <ul className={styles.picks}>
            {picks.map((pick) => {
              const range = depthLabel(pick.depthRange);
              return (
                <li key={pick.geologicIntervalId} className={styles.pick}>
                  <span className={styles.pickName}>
                    {pick.geologicIntervalName}
                  </span>
                  {range && <span className={styles.pickRange}>{range}</span>}
                  <span className={styles.pickScore}>
                    confidence {pctScore(pick.score)}
                  </span>
                  {pick.rank === 1 && (
                    <Tag type="green" size="sm">
                      Recommended
                    </Tag>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
};

// Agent Output (inline): expandable agent-run audit for the subject UWI; each
// row expands to Inputs / Reasoning / Output. POST /queries/pre_drill_agent_runs.
export const AgentOutputTable: FC<AgentOutputTableProps> = ({ uwi14 }) => {
  const [resource, fetchRuns] = useAtom(preDrillAgentOutputAtom);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchRuns({
      type: 'POST',
      url: '/queries/pre_drill_agent_runs',
      data: {
        query: { uwi14, sort_attribute: 'created_at', sort_direction: 'DESC' },
      },
    });
  }, [uwi14, fetchRuns]);

  const { loading, data, error } = formatMutationResource(resource);
  const runs = data?.results ?? [];
  const runIndex = new Map(runs.map((run) => [String(run.id), run]));

  const rows: DisplayRow[] = runs.map((run) => ({
    id: String(run.id),
    sequence: run.sequence,
    dataCategory:
      DATA_CATEGORY_LABELS[run.dataCategory] ?? humanize(run.dataCategory),
    action: humanize(run.action),
    summary: runSummary(run),
    user: userLabel(run),
    date: formatRunDate(run.createdAt),
  }));

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className={styles.wrapper}>
      <h3 className={styles.heading}>Agent Output</h3>

      {!loading && error?.type && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Failed to load agent output"
        />
      )}
      {loading && !data && (
        <InlineLoading description="Loading agent output…" />
      )}
      {!loading && !error?.type && rows.length === 0 && (
        <p className={styles.muted}>No agent runs yet.</p>
      )}

      {rows.length > 0 && (
        <DataTable rows={rows} headers={HEADERS}>
          {({
            rows: dtRows,
            headers,
            getHeaderProps,
            getTableProps,
          }: DataTableRenderProps<DisplayRow, typeof HEADERS>) => (
            <TableContainer>
              <Table {...getTableProps()} size="sm">
                <TableHead>
                  <TableRow>
                    <TableExpandHeader aria-label="expand row" />
                    {headers.map((h) => (
                      <TableHeader
                        {...getHeaderProps({ header: h })}
                        key={h.key}
                      >
                        {h.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dtRows.map((row) => {
                    const run = runIndex.get(row.id);
                    if (!run) return null;
                    const isExpanded = expanded.has(row.id);
                    return (
                      <Fragment key={row.id}>
                        <TableExpandRow
                          isExpanded={isExpanded}
                          onExpand={() => toggle(row.id)}
                          aria-label={`Toggle run ${row.id} details`}
                        >
                          {row.cells.map((cell) => (
                            <TableCell key={cell.id}>
                              {cell.value as unknown as ReactNode}
                            </TableCell>
                          ))}
                        </TableExpandRow>
                        {isExpanded && (
                          <TableExpandedRow colSpan={HEADERS.length + 1}>
                            <div className={styles.expanded}>
                              <InputsPanel run={run} />
                              <ReasoningPanel run={run} />
                              <OutputPanel run={run} />
                            </div>
                          </TableExpandedRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      )}
    </div>
  );
};
